import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { NavbarLogo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Plus, LayoutDashboard, MessageSquare, User as UserIcon, LogOut, Search, Bus, Globe2, Languages } from "lucide-react";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: "/search", label: t("nav.searchRide"), icon: Search },
    { to: "/bus", label: t("nav.bus"), icon: Bus },
    { to: "/explore", label: t("nav.explore"), icon: Globe2 },
  ];

  const handleLogout = async () => { await logout(); navigate("/"); };
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const LangToggle = ({ className = "" }) => (
    <button data-testid="language-toggle" onClick={toggle} className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-sm text-white/80 hover:bg-white/6 ${className}`} aria-label="Toggle language">
      <Languages size={16} className="text-[#22D3EE]" />
      <span className="font-semibold">{lang === "en" ? "ไทย" : "EN"}</span>
    </button>
  );

  return (
    <header className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-[rgba(7,8,21,0.6)] border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2">
          <NavbarLogo />
          <span className="font-display text-lg font-semibold text-white">Shareride<span className="text-[#22D3EE]">.co</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className={`px-3 py-2 rounded-lg text-sm transition-[background-color,color] duration-200 hover:bg-white/6 ${location.pathname === l.to ? "text-white" : "text-white/75"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <LangToggle />
          <Button data-testid="navbar-publish-ride-button" onClick={() => navigate("/publish")} className="bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-1 h-10">
            <Plus size={16} /> {t("nav.publish")}
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="navbar-profile-menu" className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[rgba(34,211,238,0.55)]">
                  <Avatar className="h-10 w-10 ring-2 ring-[rgba(34,211,238,0.35)]">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#3B82F6] text-white text-sm">{initials}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass-solid text-white w-56">
                <DropdownMenuLabel className="truncate">{user.name}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} className="gap-2 cursor-pointer"><LayoutDashboard size={15} /> {t("nav.dashboard")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/messages")} className="gap-2 cursor-pointer"><MessageSquare size={15} /> {t("nav.messages")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} className="gap-2 cursor-pointer"><UserIcon size={15} /> {t("nav.profile")}</DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem data-testid="navbar-logout" onClick={handleLogout} className="gap-2 cursor-pointer text-[#FB7185]"><LogOut size={15} /> {t("nav.logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="navbar-login-button" variant="ghost" onClick={() => navigate("/auth")} className="text-white hover:bg-white/6 h-10">{t("nav.login")}</Button>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          <LangToggle />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white" aria-label="Open menu"><Menu /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="glass-solid text-white border-white/10 w-72">
              <div className="flex items-center gap-2 mb-6 mt-2"><NavbarLogo /><span className="font-display text-lg">Shareride.co</span></div>
              <div className="flex flex-col gap-1">
                {navLinks.map((l) => (
                  <button key={l.to} onClick={() => { navigate(l.to); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left"><l.icon size={16} className="text-[#22D3EE]" /> {l.label}</button>
                ))}
                <button onClick={() => { navigate("/publish"); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg bg-[hsl(var(--primary))] text-white mt-2"><Plus size={16} /> {t("nav.publish")}</button>
                {user ? (
                  <>
                    <button onClick={() => { navigate("/dashboard"); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left"><LayoutDashboard size={16} /> {t("nav.dashboard")}</button>
                    <button onClick={() => { navigate("/messages"); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left"><MessageSquare size={16} /> {t("nav.messages")}</button>
                    <button onClick={() => { navigate(`/profile/${user.user_id}`); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left"><UserIcon size={16} /> {t("nav.profile")}</button>
                    <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left text-[#FB7185]"><LogOut size={16} /> {t("nav.logout")}</button>
                  </>
                ) : (
                  <button onClick={() => { navigate("/auth"); setMobileOpen(false); }} className="flex items-center gap-2 px-3 py-3 rounded-lg hover:bg-white/6 text-left"><UserIcon size={16} /> {t("nav.login")}</button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
